import {
  ascii,
  decodeText,
  sniffImageType,
  view,
  type BlockReader,
  type EmbeddedPicture,
} from './reader';
import type { MediaChapter, MediaInfo } from './index';

/**
 * Chapters and cover art from an ID3v2 tag — the tag at the front of an MP3
 * (and occasionally of a FLAC or AAC file).
 *
 * Chapters are `CHAP` frames: a start and end time in milliseconds, with the
 * title in an embedded `TIT2` frame. Cover art is `APIC` (`PIC` in v2.2); the
 * "front cover" picture is preferred when there are several.
 *
 * The whole tag is read in one request. It sits at the start of the file and
 * is usually small; when it is large, that is the cover art it carries.
 */

const MAX_TAG_BYTES = 32 * 1024 * 1024;
const FRONT_COVER = 3;

export interface Id3Result extends MediaInfo {
  /** Where the audio (or the next container) starts, after the tag. */
  tagEnd: number;
}

export async function readId3(r: BlockReader): Promise<Id3Result> {
  const head = await r.bytes(0, 10);
  if (head.length < 10 || ascii(head, 0, 3) !== 'ID3') return { chapters: [], tagEnd: 0 };

  const major = head[3] ?? 0;
  const flags = head[5] ?? 0;
  const size = syncsafe(head, 6);
  const tagEnd = 10 + size + (major === 4 && flags & 0x10 ? 10 : 0); // v2.4 footer
  if (major < 2 || major > 4 || size > MAX_TAG_BYTES) return { chapters: [], tagEnd };

  let tag = await r.bytes(10, size);
  // v2.2/2.3 apply unsynchronisation to the whole tag; v2.4 does it per frame.
  if (major < 4 && flags & 0x80) tag = resync(tag);

  let at = 0;
  if (major >= 3 && flags & 0x40 && tag.length >= 4) {
    at = major === 4 ? syncsafe(tag, 0) : view(tag).getUint32(0) + 4;
  }

  const frames = readFrames(tag, at, tag.length, major);
  const chapters = frames
    .filter((f) => f.id === 'CHAP')
    .map((f) => chapterFrame(f.body, major))
    .filter((c): c is MediaChapter => c !== null)
    .sort((a, b) => a.startSec - b.startSec);

  const pictures = frames
    .filter((f) => f.id === 'APIC' || f.id === 'PIC')
    .map((f) => pictureFrame(f.body, f.id === 'PIC'))
    .filter((p): p is EmbeddedPicture & { type: number } => p !== null);
  const picture = pictures.find((p) => p.type === FRONT_COVER) ?? pictures[0];

  return {
    chapters: chapters.map((c, i) => ({ ...c, title: c.title || `Chapter ${i + 1}` })),
    ...(picture ? { cover: { mimeType: picture.mimeType, data: picture.data } } : {}),
    tagEnd,
  };
}

interface Frame {
  id: string;
  body: Uint8Array;
}

function readFrames(tag: Uint8Array, start: number, end: number, major: number): Frame[] {
  const idLength = major === 2 ? 3 : 4;
  const headerLength = major === 2 ? 6 : 10;
  const frames: Frame[] = [];

  let at = start;
  while (at + headerLength <= end) {
    const id = ascii(tag, at, idLength);
    if (!/^[A-Z0-9]+$/.test(id)) break; // padding, or the end of valid data

    const size =
      major === 2 ? ((tag[at + 3] ?? 0) << 16) | ((tag[at + 4] ?? 0) << 8) | (tag[at + 5] ?? 0)
      : major === 4 ? syncsafe(tag, at + 4)
      : view(tag).getUint32(at + 4);
    const frameFlags = major === 2 ? 0 : view(tag).getUint16(at + 8);
    let body = tag.subarray(at + headerLength, Math.min(end, at + headerLength + size));
    at += headerLength + size;

    if (major === 3) {
      if (frameFlags & 0x00c0) continue; // compressed or encrypted
      if (frameFlags & 0x0020) body = body.subarray(1); // group id
    } else if (major === 4) {
      if (frameFlags & 0x000c) continue; // compressed or encrypted
      if (frameFlags & 0x0040) body = body.subarray(1); // group id
      if (frameFlags & 0x0001) body = body.subarray(4); // data length indicator
      if (frameFlags & 0x0002) body = resync(body);
    }
    frames.push({ id, body });
  }
  return frames;
}

/** CHAP: element id, start ms, end ms, start/end byte offsets, sub-frames. */
function chapterFrame(body: Uint8Array, major: number): MediaChapter | null {
  const idEnd = body.indexOf(0);
  if (idEnd === -1 || idEnd + 17 > body.length) return null;
  const startMs = view(body).getUint32(idEnd + 1);
  const sub = readFrames(body, idEnd + 17, body.length, major);
  const title = sub.find((f) => f.id === 'TIT2');
  return { title: title ? textFrame(title.body) : '', startSec: startMs / 1000 };
}

function textFrame(body: Uint8Array): string {
  if (body.length < 1) return '';
  return decodeText(body.subarray(1), encodingName(body[0] ?? 0, body.subarray(1)));
}

/** APIC: encoding, MIME type, picture type, description, data.
 *  PIC (v2.2): encoding, three-letter format, picture type, description, data. */
function pictureFrame(body: Uint8Array, v22: boolean): (EmbeddedPicture & { type: number }) | null {
  const encoding = body[0] ?? 0;
  let at = 1;
  let mime: string;
  if (v22) {
    const format = ascii(body, 1, 3).toUpperCase();
    mime = format === 'PNG' ? 'image/png' : 'image/jpeg';
    at = 4;
  } else {
    const mimeEnd = body.indexOf(0, at);
    if (mimeEnd === -1) return null;
    mime = ascii(body, at, mimeEnd - at).toLowerCase();
    at = mimeEnd + 1;
  }
  const type = body[at] ?? 0;
  at += 1;
  at = afterTerminator(body, at, encoding);
  if (at >= body.length) return null;

  const data = body.subarray(at);
  if (!mime.startsWith('image/')) mime = mime === 'jpg' ? 'image/jpeg' : sniffImageType(data);
  return { mimeType: mime, data, type };
}

/** Skips a null-terminated string: one zero byte, or two aligned zero bytes
 *  for the UTF-16 encodings. */
function afterTerminator(body: Uint8Array, at: number, encoding: number): number {
  if (encoding === 1 || encoding === 2) {
    for (let i = at; i + 1 < body.length; i += 2) {
      if (body[i] === 0 && body[i + 1] === 0) return i + 2;
    }
    return body.length;
  }
  const end = body.indexOf(0, at);
  return end === -1 ? body.length : end + 1;
}

function encodingName(encoding: number, text: Uint8Array): string {
  if (encoding === 1) return text[0] === 0xfe ? 'utf-16be' : 'utf-16le'; // BOM decides
  if (encoding === 2) return 'utf-16be';
  if (encoding === 3) return 'utf-8';
  return 'latin1';
}

function syncsafe(b: Uint8Array, at: number): number {
  return (
    (((b[at] ?? 0) & 0x7f) << 21) |
    (((b[at + 1] ?? 0) & 0x7f) << 14) |
    (((b[at + 2] ?? 0) & 0x7f) << 7) |
    ((b[at + 3] ?? 0) & 0x7f)
  );
}

/** Undoes unsynchronisation: every 0xFF 0x00 pair was written for a 0xFF. */
function resync(b: Uint8Array): Uint8Array {
  const out = new Uint8Array(b.length);
  let n = 0;
  for (let i = 0; i < b.length; i++) {
    out[n++] = b[i] ?? 0;
    if (b[i] === 0xff && b[i + 1] === 0x00) i++;
  }
  return out.subarray(0, n);
}
